import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";

const TITLES = {
  test_drive: { title: "Book a Test Drive", desc: "Pick your date and time — we'll have the keys ready." },
  quote: { title: "Get a Quote", desc: "Tell us about your trade-in and down payment for a full quote." },
  general_inquiry: { title: "I'm Interested", desc: "Questions about this vehicle or our inventory? We respond fast." },
};

const inputCls = "bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus-visible:ring-[#FF6B00] rounded-sm h-11";
const selectCls =
  "h-11 w-full rounded-sm border border-zinc-300 bg-white px-3 text-sm text-zinc-900 focus:outline-none focus:ring-1 focus:ring-[#FF6B00]";

const EMPTY = {
  name: "", email: "", phone: "",
  preferred_date: "", preferred_time: "",
  trade_in_details: "", down_payment: "",
  message: "",
};

export const LeadCaptureModal = ({ open, onOpenChange, type = "general_inquiry", vehicle = null }) => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const meta = TITLES[type] || TITLES.general_inquiry;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads", {
        lead_type: type,
        vehicle_id: vehicle?.id || null,
        vehicle_title: vehicle?.title || null,
        name: form.name,
        email: form.email,
        phone: form.phone,
        preferred_date: form.preferred_date || null,
        preferred_time: form.preferred_time || null,
        trade_in_details: form.trade_in_details || null,
        down_payment: form.down_payment ? Number(form.down_payment) : null,
        message: form.message,
      });
      toast.success("Request received! Our team will reach out shortly.");
      setForm(EMPTY);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-zinc-200 text-zinc-900 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="lead-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black uppercase text-zinc-900" data-testid="lead-modal-title">
            {meta.title}
          </DialogTitle>
          <DialogDescription className="text-zinc-500">{meta.desc}</DialogDescription>
          {vehicle && (
            <p className="text-xs font-mono uppercase tracking-wider text-[#FF6B00] pt-1" data-testid="lead-modal-vehicle">
              {vehicle.title} — ${Number(vehicle.price).toLocaleString()}
            </p>
          )}
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Full Name *</Label>
              <Input required value={form.name} onChange={set("name")} className={inputCls} placeholder="John Carter" data-testid="lead-name-input" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Phone *</Label>
              <Input required value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(555) 123-4567" data-testid="lead-phone-input" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Email *</Label>
            <Input required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" data-testid="lead-email-input" />
          </div>

          {type === "test_drive" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Preferred Date *</Label>
                <Input required type="date" value={form.preferred_date} onChange={set("preferred_date")} className={inputCls} data-testid="lead-date-input" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Time Slot *</Label>
                <select required value={form.preferred_time} onChange={set("preferred_time")} className={selectCls} data-testid="lead-time-select">
                  <option value="">Select time</option>
                  {["9:00 AM", "11:00 AM", "1:00 PM", "3:00 PM", "5:00 PM"].map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {type === "quote" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Trade-In (Year/Make/Model)</Label>
                <Input value={form.trade_in_details} onChange={set("trade_in_details")} className={inputCls} placeholder="2019 Ford F-250" data-testid="lead-tradein-input" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Down Payment ($)</Label>
                <Input type="number" min={0} value={form.down_payment} onChange={set("down_payment")} className={inputCls} placeholder="10000" data-testid="lead-downpayment-input" />
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Message</Label>
            <Textarea
              value={form.message}
              onChange={set("message")}
              className={`${inputCls} min-h-[90px]`}
              placeholder="Anything we should know?"
              data-testid="lead-message-input"
            />
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-12 bg-[#FF6B00] hover:bg-[#FF8533] text-black font-bold uppercase tracking-wider rounded-sm"
            data-testid="lead-submit-button"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit Request"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
